import { APP_LIFECYCLE_PHASE_PRIORITY, getAppLifecyclePhase } from "./app-lifecycle";
import type { AppLifecyclePhase } from "./types";

/** Safe public app row returned by Convex `cityscape:cityscape`. */
export interface PublicCityscapeApp {
	_id: string;
	_creationTime: number;
	name: string;
	tagline?: string;
	concept?: string;
	bundleId?: string;
	score?: number;
	status: string;
	computedHealth: string;
	daysSinceActivity: number;
	deployment?: {
		bundleIdRegistered: boolean;
		appCreated: boolean;
		versionCreated: boolean;
		buildUploaded: boolean;
		submittedForReview: boolean;
		isLive: boolean;
	};
	mvpProgressPercent: number;
	gtmProgressPercent: number;
	iconUrl?: string;
	appStoreUrl?: string;
}

export type PublicBuildingHealth = "thriving" | "healthy" | "stale" | "neglected" | "critical";

export interface PublicPortfolioAppMetrics {
	bundleId: string;
	mrr?: number;
	revenue30d?: number;
	downloads30d?: number;
	activeSubscribers?: number;
	freeTrialers?: number;
	rating?: number;
	ratingCount?: number;
}

export type PublicPortfolioMetricsSlice = PublicPortfolioAppMetrics;

export interface PublicBuildingState {
	app: PublicCityscapeApp;
	construction: number;
	mvpProgress: number;
	deployProgress: number;
	growthScale: number;
	color: string;
	gridPos: [number, number];
	phase: AppLifecyclePhase;
	health: PublicBuildingHealth;
	daysSinceActivity: number;
}

export interface PublicCityState {
	buildings: PublicBuildingState[];
	positionMap: Map<string, [number, number]>;
	stats: {
		total: number;
		live: number;
		building: number;
		newApps: number;
	};
}

export type BuildPublicCityStateOptions = {
	portfolioMetrics?: Map<string, PublicPortfolioMetricsSlice>;
};

export const PUBLIC_PHASE_COLORS: Record<AppLifecyclePhase, string> = {
	new: "#71717a",
	building: "#f59e0b",
	deployed: "#007AFF",
	live: "#007AFF",
	growing: "#a855f7",
};

const PUBLIC_BUILDING_HEALTH_LEVELS = ["thriving", "healthy", "stale", "neglected", "critical"] as const satisfies readonly PublicBuildingHealth[];

const DEPLOY_STEP_KEYS = [
	"bundleIdRegistered",
	"appCreated",
	"versionCreated",
	"buildUploaded",
	"submittedForReview",
	"isLive",
] as const satisfies readonly (keyof NonNullable<PublicCityscapeApp["deployment"]>)[];

const GRID_SPACING = 5.5;

/** Lowercase trim — stable map key for App Store portfolio JSON bundle ids. */
export function normalizePublicBundleId(value?: string | null): string {
	return (value ?? "").trim().toLowerCase();
}

function coercePublicBuildingHealth(value: string | undefined): PublicBuildingHealth {
	if (value && (PUBLIC_BUILDING_HEALTH_LEVELS as readonly string[]).includes(value)) {
		return value as PublicBuildingHealth;
	}
	return "healthy";
}

function getPublicDeploymentProgress(deployment?: PublicCityscapeApp["deployment"]): number {
	if (!deployment) return 0;
	const done = DEPLOY_STEP_KEYS.filter((key) => deployment[key]).length;
	return done / DEPLOY_STEP_KEYS.length;
}

function lookupPortfolioMetrics(app: PublicCityscapeApp, portfolioMetrics?: Map<string, PublicPortfolioMetricsSlice>): PublicPortfolioMetricsSlice | null {
	const key = normalizePublicBundleId(app.bundleId);
	if (!key || !portfolioMetrics) return null;
	return portfolioMetrics.get(key) ?? null;
}

/** Row-major rectangular pack ordered by lifecycle phase (see point-public/docs/iso-spec.md). */
function assignRectangularPositions(entries: { id: string; phase: AppLifecyclePhase }[]): Map<string, [number, number]> {
	const sorted = [...entries].sort((a, b) => {
		const phaseDelta = APP_LIFECYCLE_PHASE_PRIORITY[a.phase] - APP_LIFECYCLE_PHASE_PRIORITY[b.phase];
		if (phaseDelta !== 0) return phaseDelta;
		return a.id.localeCompare(b.id);
	});

	const cols = Math.max(3, Math.ceil(Math.sqrt(sorted.length * 1.5)));
	const positionMap = new Map<string, [number, number]>();

	for (let index = 0; index < sorted.length; index++) {
		const entry = sorted[index];
		if (!entry) continue;
		const row = Math.floor(index / cols);
		const col = index % cols;
		positionMap.set(entry.id, [col * GRID_SPACING, row * GRID_SPACING]);
	}

	return positionMap;
}

function computeGrowthScale(
	app: PublicCityscapeApp,
	phase: AppLifecyclePhase,
	construction: number,
	deployProgress: number,
	metrics: PublicPortfolioMetricsSlice | null,
): number {
	switch (phase) {
		case "new":
			return 1;
		case "building":
			return 1 + (app.score ? app.score / 100 : 0);
		case "deployed":
			return 2 + deployProgress * 1.5;
		case "live":
			return 3.5 + construction * 1.5;
		case "growing": {
			let growthScale = 5;
			if (metrics?.downloads30d) growthScale += Math.log10(metrics.downloads30d + 1) * 0.5;
			if (metrics?.revenue30d) growthScale += Math.log10(metrics.revenue30d + 1) * 1.2;
			if (metrics?.activeSubscribers) growthScale += Math.log10(metrics.activeSubscribers + 1);
			if (metrics?.rating && metrics.rating >= 4.5) growthScale += 1;
			else if (metrics?.rating && metrics.rating >= 4) growthScale += 0.5;
			return growthScale;
		}
		default:
			return 1;
	}
}

export function buildPublicCityState(apps: PublicCityscapeApp[], options: BuildPublicCityStateOptions = {}): PublicCityState {
	const appPhases = apps.map((app) => {
		const metrics = lookupPortfolioMetrics(app, options.portfolioMetrics);
		const hasGrowth = !!metrics && ((metrics.downloads30d ?? 0) > 0 || (metrics.revenue30d ?? 0) > 0);
		return {
			id: app._id,
			phase: getAppLifecyclePhase(app, { hasGrowth }),
		};
	});

	const positionMap = assignRectangularPositions(appPhases);

	const buildings = apps.map((app) => {
		const metrics = lookupPortfolioMetrics(app, options.portfolioMetrics);
		const hasGrowth = !!metrics && ((metrics.downloads30d ?? 0) > 0 || (metrics.revenue30d ?? 0) > 0);
		const phase = getAppLifecyclePhase(app, { hasGrowth });
		const construction = Math.max(0, Math.min(1, app.gtmProgressPercent / 100));
		const mvpProgress = Math.max(0, Math.min(1, app.mvpProgressPercent / 100));
		const deployProgress = getPublicDeploymentProgress(app.deployment);

		return {
			app,
			construction,
			mvpProgress,
			deployProgress,
			growthScale: computeGrowthScale(app, phase, construction, deployProgress, metrics),
			color: PUBLIC_PHASE_COLORS[phase] ?? "#71717a",
			gridPos: positionMap.get(app._id) ?? [0, 0],
			phase,
			health: coercePublicBuildingHealth(app.computedHealth),
			daysSinceActivity: app.daysSinceActivity ?? 0,
		};
	});

	return {
		buildings,
		positionMap,
		stats: {
			total: buildings.length,
			live: buildings.filter((building) => building.phase === "live" || building.phase === "growing").length,
			building: buildings.filter((building) => building.phase === "building" || building.phase === "deployed").length,
			newApps: buildings.filter((building) => building.phase === "new").length,
		},
	};
}
